import React, { FC, useCallback, useState } from 'react';
import { Button, notification, Spin } from 'antd';
import { CloseCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SURVEY_ARCHIVE_QP } from 'shared/constants/poll';
import {
  useGetPollByIdQuery,
  useLazyGetPollResultsQuery,
  usePublicateAutoPollMutation,
  useUpdatePollStatusMutation,
} from 'app/services/edpApiV1/poll';
import { TQuestionInPoll } from 'shared/types/poll.types';
import { PollTabs } from 'shared/enums/poll';
import { addNotifications, createPollExcelCommonInfo } from 'shared/helpers/utils';
import { checkStartDatePoll } from 'shared/helpers/date';
import * as XLSX from 'xlsx-js-style';
import { WorkSheet } from 'xlsx-js-style';
import { PICKER_DATE_MASK, PICKER_TIME_MASK } from 'shared/constants';
import moment from 'moment';
import { uiModel } from 'entities/ui';
import { useAppDispatch } from 'app/store';
import { TPollAnswer } from '../questionDrawer/QuestionDrawer';
import style from './index.module.scss';
import ResultDrawer from './resultDrawer/ResultDrawer';
import QuestionInPollInfo from './questionInPollInfo/QuestionInPollInfo';
import CommonInfo from './commonInfo/CommonInfo';

const InfoPoll: FC = () => {
  const { id } = useParams();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState<TQuestionInPoll>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isYourAnswer, setIsYourAnswer] = useState<boolean>(false);
  const navigate = useNavigate();
  const { data: currentPoll } = useGetPollByIdQuery({ id: +id! });
  const [getPollResultsQuery, result] = useLazyGetPollResultsQuery();
  const [closePollQuery, { isLoading: isClosePollQueryLoading }] = useUpdatePollStatusMutation();
  const [publicateAutoPollQuery] = usePublicateAutoPollMutation();
  const [currentAnswer, setCurrentAnswer] = useState<TPollAnswer>();
  const dispatch = useAppDispatch();

  const onDrawerClose = () => {
    setDrawerOpen(false);
  };

  const navigateAfterSuccess = (tab: PollTabs) => {
    return () => {
      dispatch(uiModel.Tabs.actions.setActive(tab));
      navigate('/polls');
    };
  };

  const closePoll = async () => {
    await addNotifications(
      closePollQuery({ id: Number(id), status: PollTabs.ARCHIVE }),
      'Опрос завершен',
      'Ошибка при завершении опроса',
      navigateAfterSuccess(PollTabs.PUBLICATED),
    );
  };
  const publicateAutoPoll = () => {
    addNotifications(
      publicateAutoPollQuery({ id: Number(id), status: PollTabs.PUBLICATED }),
      'Опрос начат',
      'Ошибка при досрочном старте опроса',
      navigateAfterSuccess(PollTabs.PUBLICATED),
    );
  };

  const download = async () => {
    const workbook = XLSX.utils.book_new();
    let commonData = createPollExcelCommonInfo(currentPoll?.[0]);
    if (!currentPoll) {
      notification.error({ message: 'Ошибка! Отсутствуют данные по голосованию' });
      return;
    }

    for (const q of currentPoll?.[0].questions) {
      let question: TQuestionInPoll;
      if (q.isYourAnswer) {
        const yourAnswer: TPollAnswer = {
          id: NaN,
          value: '',
        };
        question = { ...q, answers: [...q.answers, yourAnswer] };
      } else {
        question = { ...q };
      }
      for (const resultAnswer of question.answers) {
        if (!resultAnswer) {
          continue;
        }
        const answer = await getPollResultsQuery({
          questionId: Number(question.id),
          answerId: isNaN(resultAnswer?.id) ? undefined : resultAnswer?.id,
          allResults: true,
          isYourAnswer: isNaN(resultAnswer?.id) ? question?.isYourAnswer : undefined,
        });
        const { data } = answer;

        if (data) {
          const userAnswers = data.data.map(elem => [
            `${elem.employee.lastName} ${elem.employee.name} ${elem.employee.middleName} / ${elem.employee.personnelNumber}`,
            elem.answer.value,
            moment(elem.date).add('3h').format(`${PICKER_DATE_MASK} ${PICKER_TIME_MASK}`),
            question.title,
          ]);
          commonData = [...commonData, ...userAnswers];
        }
      }
    }

    const newWs: WorkSheet = XLSX.utils.aoa_to_sheet(commonData);
    XLSX.utils.book_append_sheet(workbook, newWs);
    XLSX.writeFile(workbook, `${currentPoll?.[0].title}.xlsx`, { bookType: 'xlsx', bookSST: false });
  };
  const onDrawerOpen = (
    answerId: number | null,
    questionId: number,
    answer?: TPollAnswer,
    question?: TQuestionInPoll,
  ) => {
    setCurrentAnswer(answer);
    setCurrentQuestion(question);
    setDrawerOpen(true);

    if (answerId === null) {
      setIsYourAnswer(true);
      getPollResultsQuery({ questionId, isYourAnswer: true });
    } else {
      setIsYourAnswer(false);
      getPollResultsQuery({ questionId, answerId });
    }
  };

  const getAnswers = useCallback(
    (page: number, size: number, questionId: number, answerId?: number) => {
      if (isYourAnswer) {
        getPollResultsQuery({ questionId, isYourAnswer: true, page, size });
      } else {
        getPollResultsQuery({ questionId, answerId, page, size });
      }
    },
    [getPollResultsQuery, id, isYourAnswer],
  );
  return (
    <div className={style['info-poll']}>
      <div className={style.top}>
        <h3>{currentPoll?.[0].title}</h3>
        <div className={style.buttons}>
          {!searchParams.get(SURVEY_ARCHIVE_QP) && checkStartDatePoll(currentPoll?.[0].startDate) ? (
            <Button type="primary" onClick={publicateAutoPoll}>
              Начать опрос
            </Button>
          ) : null}
          {!searchParams.get(SURVEY_ARCHIVE_QP) ? (
            <Button onClick={closePoll} className={style.closePollButton} loading={isClosePollQueryLoading}>
              <CloseCircleOutlined /> Завершить опрос
            </Button>
          ) : null}
          <Button type="primary" onClick={download}>
            <UploadOutlined /> Скачать результаты
          </Button>
          <Link to="/polls">
            <Button>Закрыть</Button>
          </Link>
        </div>
      </div>
      <Spin spinning={!currentPoll}>
        {!currentPoll ? null : (
          <>
            <QuestionInPollInfo
              onDrawerOpen={onDrawerOpen}
              currentPoll={currentPoll?.[0]}
              totalAnswers={currentPoll?.[0].participantsCount}
            />
            <ResultDrawer
              onClose={onDrawerClose}
              open={drawerOpen}
              info={result.data}
              isYourAnswer={isYourAnswer}
              getAnswers={getAnswers}
              answer={currentAnswer}
              question={currentQuestion}
            />
            <CommonInfo currentPoll={currentPoll?.[0]} />
          </>
        )}
      </Spin>
    </div>
  );
};

export default InfoPoll;
